import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import ArrowbackIcon from "../../assets/icons/arrow_back.svg";
import SearchIcon from "../../assets/icons/search.svg";
import ArrowDropDownIcon from "../../assets/icons/arrow_drop_down.svg";
import InitialsCircle from "../../components/InitialsCircle";
import {
  ONBOARDING_STATUS_FILTERS,
  ONBOARDING_STATUS_META,
} from "../../constants/onboarding";
import { getCandidateApprovalSummary } from "../../store/OnboardingSlice/thunk";
import { onboardingActions } from "../../store/OnboardingSlice";
import {
  buildStoresFromSummary,
  maskAadhaarNumber,
  normalizeApprovalSummaryList,
} from "../../utils/onboardingHelpers";
import "./ReviewQueue.css";

const PAGE_SIZE = 15;

const ReviewQueue = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const storeDropdownRef = useRef(null);
  const storeSearchInputRef = useRef(null);

  const approvalSummaryResponse = useSelector(
    (state) => state.onboarding.approvalSummaryResponse,
  );
  const approvalSummaryStatus = useSelector(
    (state) => state.onboarding.approvalSummaryStatus,
  );

  const [searchText, setSearchText] = useState("");
  const [storeSearchText, setStoreSearchText] = useState("");
  const [selectedStore, setSelectedStore] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [isStoreDropdownOpen, setIsStoreDropdownOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);

  useEffect(() => {
    dispatch(getCandidateApprovalSummary({ page }));
  }, [dispatch, page]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        storeDropdownRef.current &&
        !storeDropdownRef.current.contains(event.target)
      ) {
        setIsStoreDropdownOpen(false);
        setStoreSearchText("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isStoreDropdownOpen && storeSearchInputRef.current) {
      storeSearchInputRef.current.focus();
    }
  }, [isStoreDropdownOpen]);

  const onboardingRequests = useMemo(
    () =>
      approvalSummaryStatus === "SUCCESS"
        ? normalizeApprovalSummaryList(approvalSummaryResponse)
        : [],
    [approvalSummaryResponse, approvalSummaryStatus],
  );

  const isPreviousDisabled = page <= 1 || approvalSummaryStatus === "PENDING";
  const isNextDisabled =
    approvalSummaryStatus !== "SUCCESS" ||
    onboardingRequests.length < PAGE_SIZE;

  const onPreviousPage = () => {
    if (isPreviousDisabled) return;
    setPage((prev) => Math.max(1, prev - 1));
  };

  const onNextPage = () => {
    if (isNextDisabled) return;
    setPage((prev) => prev + 1);
  };

  const onboardingStores = useMemo(
    () => buildStoresFromSummary(onboardingRequests),
    [onboardingRequests],
  );

  const selectedStoreLabel = useMemo(() => {
    if (selectedStore === "all") return "All stores";
    const store = onboardingStores.find(
      (item) => item.store_code === selectedStore,
    );
    return store ? store.store_code : "All stores";
  }, [selectedStore, onboardingStores]);

  const filteredStores = useMemo(() => {
    const normalizedSearch = storeSearchText.trim().toLowerCase();
    if (!normalizedSearch) return onboardingStores;

    return onboardingStores.filter(
      (store) =>
        store.store_code.toLowerCase().includes(normalizedSearch) ||
        store.store_name.toLowerCase().includes(normalizedSearch),
    );
  }, [storeSearchText, onboardingStores]);

  const closeStoreDropdown = () => {
    setIsStoreDropdownOpen(false);
    setStoreSearchText("");
  };

  const filteredRequests = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    const filtered = onboardingRequests.filter((request) => {
      const matchesSearch =
        !normalizedSearch ||
        request.candidate_name.toLowerCase().includes(normalizedSearch) ||
        request.id.toLowerCase().includes(normalizedSearch) ||
        String(request.aadhaar_number || "")
          .toLowerCase()
          .includes(normalizedSearch);

      const matchesStore =
        selectedStore === "all" || request.store_code === selectedStore;

      const matchesStatus = (() => {
        if (selectedStatus === "all") return true;
        if (selectedStatus === "Awaiting") {
          const normalized = String(request.status || "")
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "_");
          return (
            normalized === "awaiting" ||
            normalized === "awaiting_resubmission"
          );
        }
        return request.status === selectedStatus;
      })();

      return matchesSearch && matchesStore && matchesStatus;
    });

    return filtered.sort((a, b) => {
      const timeA = a.submitted_at ? new Date(a.submitted_at).getTime() : 0;
      const timeB = b.submitted_at ? new Date(b.submitted_at).getTime() : 0;
      return sortOrder === "desc" ? timeB - timeA : timeA - timeB;
    });
  }, [
    onboardingRequests,
    searchText,
    selectedStore,
    selectedStatus,
    sortOrder,
  ]);

  const onRequestAction = (request) => {
    const onboardingId =
      request?.onboarding_id != null ? request.onboarding_id : request.id;
    dispatch(
      onboardingActions.setSelectedReviewRequest({
        onboardingId: String(onboardingId),
        status: request?.status ?? null,
      }),
    );
    navigate(`../review-request/${onboardingId}`);
  };

  const isLoading = approvalSummaryStatus === "PENDING";

  return (
    <div className="rq-home">
      <div className="rq-head-wrap">
        <div className="rq-head-row">
          <img
            className="rq-back-ico"
            src={ArrowbackIcon}
            alt="arrow-back"
            onClick={() => navigate("../../people")}
          />
          <div className="rq-head">review queue</div>
        </div>
        <div className="rq-subhead">
          New joiners waiting on your review, before they get an employee ID
        </div>
      </div>

      <div className="rq-filters">
        <div className="rq-filters-left">
          <div className="rq-search-container">
            <img src={SearchIcon} className="rq-search-icon" alt="Search" />
            <input
              type="text"
              className="rq-search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by Aadhaar Number, Name, or Request ID"
            />
          </div>

          <div className="rq-store-dropdown" ref={storeDropdownRef}>
            <button
              type="button"
              className="rq-store-trigger"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setIsStoreDropdownOpen((prev) => {
                  if (prev) setStoreSearchText("");
                  return !prev;
                });
              }}
            >
              <span className="rq-store-label">{selectedStoreLabel}</span>
              <img
                src={ArrowDropDownIcon}
                className="rq-store-arrow"
                alt="dropdown"
              />
            </button>
            {isStoreDropdownOpen && (
              <div className="rq-store-menu">
                <div className="rq-store-search-container">
                  <img
                    src={SearchIcon}
                    alt="Search"
                    className="rq-store-search-icon"
                  />
                  <input
                    ref={storeSearchInputRef}
                    type="text"
                    className="rq-store-search-input"
                    placeholder="Search..."
                    value={storeSearchText}
                    onChange={(e) => setStoreSearchText(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <ul className="rq-store-options-list">
                  {(!storeSearchText.trim() ||
                    "all stores".includes(
                      storeSearchText.trim().toLowerCase(),
                    )) && (
                    <li
                      className={`rq-store-option ${
                        selectedStore === "all" ? "selected" : ""
                      }`}
                      onClick={() => {
                        setSelectedStore("all");
                        closeStoreDropdown();
                      }}
                    >
                      All stores
                    </li>
                  )}
                  {filteredStores.map((store) => (
                    <li
                      key={store.store_code}
                      className={`rq-store-option ${
                        selectedStore === store.store_code ? "selected" : ""
                      }`}
                      onClick={() => {
                        setSelectedStore(store.store_code);
                        closeStoreDropdown();
                      }}
                    >
                      <span className="rq-store-option-code">
                        {store.store_code}
                      </span>
                      <span className="rq-store-option-name">
                        {store.store_name}
                      </span>
                    </li>
                  ))}
                  {filteredStores.length === 0 &&
                    storeSearchText.trim() &&
                    !"all stores".includes(
                      storeSearchText.trim().toLowerCase(),
                    ) && (
                      <li className="rq-store-no-options">
                        No matching stores
                      </li>
                    )}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="rq-status-filters">
          {ONBOARDING_STATUS_FILTERS.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={`rq-status-chip ${
                selectedStatus === filter.key ? "selected" : ""
              }`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setSelectedStatus(filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rq-list">
        <div className="rq-list-header">
          <div className="rq-col rq-col-candidate">Candidate</div>
          <div className="rq-col rq-col-request">Request ID</div>
          <div className="rq-col rq-col-aadhaar">Aadhaar Number</div>
          <div className="rq-col rq-col-store">Store</div>
          <div
            className="rq-col rq-col-submitted rq-sortable"
            onClick={() =>
              setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))
            }
          >
            Submitted
            <span className="rq-sort-arrow">
              {sortOrder === "desc" ? "↓" : "↑"}
            </span>
          </div>
          <div className="rq-col rq-col-status">Status</div>
          <div className="rq-col rq-col-action" />
        </div>

        <div className="rq-list-body">
          {isLoading ? (
            <div className="rq-empty">Loading onboarding requests...</div>
          ) : filteredRequests.length === 0 ? (
            <div className="rq-empty">No onboarding requests found</div>
          ) : (
            filteredRequests.map((request) => {
              const statusMeta = ONBOARDING_STATUS_META[request.status] || {};
              const statusLabel =
                request.status == null || String(request.status).trim() === ""
                  ? "-"
                  : String(request.status);

              return (
                <div className="rq-list-row" key={request.id}>
                  <div className="rq-col rq-col-candidate">
                    <InitialsCircle
                      fullName={request.candidate_name}
                      size={40}
                    />
                    <div className="rq-candidate-meta">
                      <div className="rq-candidate-name">
                        {request.candidate_name}
                      </div>
                      <div className="rq-candidate-role">
                        {request.designation}
                      </div>
                    </div>
                  </div>

                  <div className="rq-col rq-col-request">
                    <span className="rq-request-id">
                      {request?.onboarding_id != null ? request.onboarding_id : request.id}
                    </span>
                  </div>

                  <div className="rq-col rq-col-aadhaar">
                    <span className="rq-aadhaar-number">
                      {maskAadhaarNumber(request.aadhaar_number) || "-"}
                    </span>
                  </div>

                  <div className="rq-col rq-col-store">
                    <div className="rq-store-code">{request.store_code}</div>
                    <div className="rq-store-name">{request.store_name}</div>
                  </div>

                  <div className="rq-col rq-col-submitted">
                    <span className="rq-submitted">
                      {request.submitted_label ||
                        (request.submitted_at
                          ? moment(request.submitted_at).fromNow()
                          : "-")}
                    </span>
                  </div>

                  <div className="rq-col rq-col-status">
                    <span
                      className={`rq-status ${statusMeta.className || ""}`.trim()}
                    >
                      {statusLabel}
                    </span>
                  </div>

                  <div className="rq-col rq-col-action">
                    <button
                      type="button"
                      className="btn p-btn p-btn-og rq-action-btn"
                      onClick={() => onRequestAction(request)}
                    >
                      <div className="p-btn-text">
                        {statusMeta.actionLabel || "View request"}
                      </div>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="rq-pagination" role="navigation" aria-label="Pagination">
        <button
          type="button"
          className="rq-page-nav"
          onClick={onPreviousPage}
          disabled={isPreviousDisabled}
          aria-label="Previous page"
        >
          <span className="material-symbols-rounded" aria-hidden="true">
            chevron_left
          </span>
        </button>

        <span
          className="rq-page-num is-active"
          aria-label={`Page ${page}`}
          aria-current="page"
        >
          {page}
        </span>

        <button
          type="button"
          className="rq-page-nav"
          onClick={onNextPage}
          disabled={isNextDisabled}
          aria-label="Next page"
        >
          <span className="material-symbols-rounded" aria-hidden="true">
            chevron_right
          </span>
        </button>
      </div>
    </div>
  );
};

export default ReviewQueue;
